package com.islamicmedia.shared.data.auth

import com.islamicmedia.shared.util.sanitizeTypedInput

import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.auth.Auth
import io.github.jan.supabase.auth.OtpType
import io.github.jan.supabase.auth.auth
import io.github.jan.supabase.auth.providers.builtin.Email
import io.github.jan.supabase.auth.status.SessionStatus
import io.github.jan.supabase.createSupabaseClient
import io.github.jan.supabase.postgrest.Postgrest
import io.github.jan.supabase.postgrest.from
import com.islamicmedia.shared.data.api.createHttpClient
import com.islamicmedia.shared.data.api.serverBaseUrl
import io.ktor.client.request.delete
import io.ktor.client.request.header
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.map
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

object SupabaseConfig {
    val URL: String get() = com.islamicmedia.shared.AppConfig.SUPABASE_URL
    val KEY: String get() = com.islamicmedia.shared.AppConfig.SUPABASE_ANON_KEY
}

data class AuthUser(
    val id: String,
    val email: String?,
    val photoUrl: String? = null,
    val displayName: String? = null,
    /** When the ACCOUNT was created (not this session). The only way to tell a first-ever OAuth
     *  sign-in from a returning one — OAuth has no success callback of its own, so `sign_up` for
     *  Google/Apple is inferred from this. Null when the session carries no timestamp. */
    val createdAtEpochMs: Long? = null,
    /** Supabase's `app_metadata.provider`: "email", "google", "apple". Authoritative regardless of
     *  which flow (native sheet or browser round-trip) produced the session. */
    val provider: String? = null
)

sealed class AuthState {
    data object Loading : AuthState()
    data object NotAuthenticated : AuthState()
    data class Authenticated(val user: AuthUser) : AuthState()
}

@Serializable
data class ApprovedUser(
    val id: String = "",
    val email: String,
    @SerialName("registration_date") val registrationDate: String? = null,
    @SerialName("expiry_date") val expiryDate: String? = null,
    val notes: String? = null
)

class SupabaseAuthService private constructor() {
    companion object {
        val instance: SupabaseAuthService by lazy { SupabaseAuthService() }
    }

    internal val client: SupabaseClient = createSupabaseClient(
        supabaseUrl = SupabaseConfig.URL,
        supabaseKey = SupabaseConfig.KEY
    ) {
        // Admin dashboard pulls heavy per-row JSON (result_json) for hundreds of analyses,
        // so give requests a generous timeout (default is 10s).
        requestTimeout = kotlin.time.Duration.parse("120s")
        install(Auth) {
            scheme = "islamicmedia"
            host = "auth-callback"
            // Load-bearing, and the direct cause of the `auth_no_session` error reports.
            //
            // On ANDROID, supabase-kt registers a ProcessLifecycleOwner observer whose onStop runs
            // `setSessionStatus(SessionStatus.Initializing)` the moment the app is backgrounded
            // (Auth/src/androidMain/.../setupPlatform.kt). Every token accessor reads through
            // `currentSessionOrNull()`, which returns null for ANY status that isn't
            // `Authenticated` (Auth.kt:458) — so a perfectly valid, on-disk session reads back as
            // "no session at all" for as long as the app is in the background.
            //
            // This app deliberately runs analyses in the background, so the SSE `complete` event
            // lands squarely inside that window: /api/cached-result then goes out with NO
            // Authorization header, the server 401s, and the collection fails with
            // `auth_no_session` AFTER the analysis has already run and been billed.
            //
            // It also explains why forcing a session refresh there never fixed it:
            // `refreshCurrentSession()` reads the SAME null-returning accessor, so it is
            // structurally incapable of repairing an `Initializing` status.
            //
            // Turning the callbacks off leaves auto-refresh running while backgrounded — which is
            // what we want for background analyses — and the disabled branch still calls
            // `initDone()`, so initialization completes normally. No-op on iOS/web.
            enableLifecycleCallbacks = false
        }
        install(Postgrest)
    }

    val authState: Flow<AuthState> = client.auth.sessionStatus.map { status ->
        when (status) {
            is SessionStatus.Authenticated -> {
                val user = status.session.user
                val photoUrl = user?.userMetadata?.get("avatar_url")?.toString()
                    ?: user?.userMetadata?.get("picture")?.toString()
                val displayName = user?.userMetadata?.get("full_name")?.toString()
                    ?: user?.userMetadata?.get("name")?.toString()
                AuthState.Authenticated(
                    AuthUser(
                        id = user?.id ?: "",
                        email = user?.email,
                        photoUrl = photoUrl?.removeSurrounding("\""),
                        displayName = displayName?.removeSurrounding("\""),
                        createdAtEpochMs = try { user?.createdAt?.toEpochMilliseconds() } catch (_: Exception) { null },
                        provider = user?.appMetadata?.get("provider")?.toString()?.removeSurrounding("\"")
                    )
                )
            }
            is SessionStatus.NotAuthenticated -> AuthState.NotAuthenticated
            is SessionStatus.Initializing -> AuthState.Loading
            else -> AuthState.NotAuthenticated
        }
    }

    val currentUser: AuthUser?
        get() = client.auth.currentUserOrNull()?.let { user ->
            AuthUser(id = user.id, email = user.email)
        }

    val isLoggedIn: Boolean
        get() = client.auth.currentUserOrNull() != null

    /**
     * True only when a real SESSION exists (access token present). Unlike [isLoggedIn],
     * this is false right after an unconfirmed email signup — supabase-kt keeps the
     * created user but issues no session until the email is verified.
     */
    val hasActiveSession: Boolean
        get() = try { client.auth.currentAccessTokenOrNull() != null } catch (_: Exception) { false }

    val accessToken: String?
        get() = try { client.auth.currentAccessTokenOrNull() } catch (_: Exception) { null }

    /**
     * Suspend until a token is actually readable, up to [timeoutMs]. Returns null on timeout.
     *
     * Exists because "no token right now" is not the same as "signed out". The session status
     * passes through [SessionStatus.Initializing] (app start, and on Android every background
     * excursion before [enableLifecycleCallbacks] was turned off) and [SessionStatus.RefreshFailure]
     * (a refresh that failed on an already-expired token, retried every 10s). In BOTH the session is
     * intact and a token becomes readable shortly afterwards — but every accessor reads through
     * `currentSessionOrNull()`, which returns null for anything that isn't `Authenticated`, so a
     * caller that asks once and gives up concludes the user is signed out and reports an error.
     *
     * Deliberately does NOT refresh: the callers that need this are recovering from a state where a
     * refresh cannot help (it reads the same null accessor), and firing one here would rotate the
     * refresh token under whatever else is already in flight.
     */
    suspend fun awaitAccessToken(timeoutMs: Long): String? {
        accessToken?.let { return it }
        return try {
            kotlinx.coroutines.withTimeoutOrNull(timeoutMs) {
                client.auth.sessionStatus.first { it is SessionStatus.Authenticated }
                accessToken
            }
        } catch (_: Exception) {
            null
        }
    }

    /** Force refresh the session and return a fresh token. Call this before important API calls. */
    suspend fun getFreshAccessToken(): String? {
        return try {
            client.auth.refreshCurrentSession()
            client.auth.currentAccessTokenOrNull()
        } catch (_: Exception) {
            // If refresh fails, return whatever we have
            try { client.auth.currentAccessTokenOrNull() } catch (_: Exception) { null }
        }
    }

    /**
     * Trim whitespace off an address before it reaches GoTrue.
     *
     * Load-bearing, not defensive tidying. GoTrue rejects a trailing space with
     * "Unable to validate email address: invalid format" — which is indistinguishable, on screen,
     * from a perfectly typed address, because the space is invisible in the field. iOS keyboards
     * append a trailing space after autocorrect/suggestion-bar taps where Android's don't, so this
     * surfaced as an iOS-only signup failure on an address that worked fine from Android.
     *
     * Normalised HERE rather than at each call site: signup, sign-in, verify, resend, approval
     * check and password reset all take a raw address, and the ViewModel trims firstName/lastName
     * but never the email. One choke point covers every caller on all three platforms.
     *
     * Case is deliberately left alone — GoTrue lowercases addresses itself, so doing it here would
     * only risk diverging from whatever the server decides.
     *
     * Uses sanitizeTypedInput(), NOT trim(): the real culprit turned out to be an Arabic keyboard
     * planting bidi marks (U+200E/U+200F) around the Latin address. Those are invisible in the
     * field and trim() does not touch them — Char.isWhitespace() is false for every one, and for
     * the non-breaking spaces too.
     */
    private fun String.normalizeEmail(): String = sanitizeTypedInput()

    suspend fun signUpWithEmail(email: String, password: String, fullName: String? = null): Result<AuthUser> {
        val email = email.normalizeEmail()
        return try {
            // Confirmation email must redirect to an https page: the islamicmedia:// scheme
            // dead-ends (blank page) if the email is opened on desktop. /auth-callback bounces
            // into the app on mobile and lands on the web on desktop. OAuth keeps islamicmedia://.
            client.auth.signUpWith(Email, redirectUrl = "${com.islamicmedia.shared.AppConfig.WEB_BASE_URL}/auth-callback") {
                this.email = email
                this.password = password
                if (fullName != null) {
                    this.data = kotlinx.serialization.json.buildJsonObject {
                        put("full_name", kotlinx.serialization.json.JsonPrimitive(fullName))
                    }
                }
            }
            val user = client.auth.currentUserOrNull()
            if (user != null) {
                Result.success(AuthUser(id = user.id, email = user.email))
            } else {
                // Sign up succeeded but email confirmation is required
                Result.failure(Exception("email_not_confirmed"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    suspend fun signInWithEmail(email: String, password: String): Result<AuthUser> {
        val email = email.normalizeEmail()
        return try {
            client.auth.signInWith(Email) {
                this.email = email
                this.password = password
            }
            val user = client.auth.currentUserOrNull()
            if (user != null) {
                Result.success(AuthUser(id = user.id, email = user.email))
            } else {
                Result.failure(Exception("Sign in failed"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Verify a 6-digit signup confirmation code (email OTP). On success the SDK
     * establishes a session, so [authState] emits Authenticated and the app routes
     * to the main screen. Requires the Supabase "Confirm signup" email template to
     * surface {{ .Token }} (the code) — same token a magic link would carry.
     */
    suspend fun verifyEmailCode(email: String, token: String): Result<AuthUser> {
        val email = email.normalizeEmail()
        return try {
            client.auth.verifyEmailOtp(
                type = OtpType.Email.SIGNUP,
                email = email.trim(),
                token = token.trim(),
            )
            val user = client.auth.currentUserOrNull()
            if (user != null) Result.success(AuthUser(id = user.id, email = user.email))
            else Result.failure(Exception("Verification failed"))
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /** Resend the signup confirmation code to [email]. Rate-limited by Supabase. */
    suspend fun resendSignupCode(email: String): Result<Unit> {
        val email = email.normalizeEmail()
        return try {
            client.auth.resendEmail(OtpType.Email.SIGNUP, email.trim())
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * On mobile this is the NATIVE account sheet (Credential Manager on Android, the GoogleSignIn
     * SDK on iOS): it returns an ID token in-process and completes the session before returning,
     * so unlike the web path there IS a meaningful result here. On the web — and on a mobile build
     * whose client ID is unset — it falls back to the browser redirect, which hands off and returns
     * through the deep link instead. See [platformSignInWithGoogle].
     *
     * @param redirectUrl where to send the browser back to after OAuth. Only consulted on the
     * browser path. Pass a value ONLY when you need to override where the round-trip returns
     * (e.g. the web `/invite?t=…` page, which must come back to itself or it loses the token).
     * Leave it null everywhere else — null means "use the platform default", not "no redirect".
     */
    suspend fun signInWithGoogle(redirectUrl: String? = null): Result<Unit> {
        return try {
            platformSignInWithGoogle(client, redirectUrl)
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    suspend fun signInWithApple(): Result<Unit> {
        return try {
            platformSignInWithApple(client)
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    suspend fun checkApproval(email: String): Boolean {
        return try {
            val trimmed = email.trim()
            val results = client.from("approved_users")
                .select {
                    filter {
                        ilike("email", trimmed)
                    }
                }
                .decodeList<ApprovedUser>()

            results.isNotEmpty()
        } catch (e: Exception) {
            println("checkApproval error for $email: ${e.message}")
            false
        }
    }

    suspend fun signOut(): Result<Unit> {
        return try {
            client.auth.signOut()
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Clear only the locally-stored session (no network revoke). Used by the dev backend switcher:
     * the persisted session belongs to the OLD project and the new backend would reject it, so we
     * drop it before restarting. Best-effort — failures are swallowed since we restart regardless.
     */
    suspend fun clearLocalSession() {
        try { client.auth.signOut(io.github.jan.supabase.auth.SignOutScope.LOCAL) } catch (_: Exception) {}
    }

    /** Whether the current user signed in with email/password (vs OAuth like Google). */
    val currentUserUsesEmailAuth: Boolean
        get() = try {
            val user = client.auth.currentUserOrNull() ?: return false
            // Supabase marks OAuth users with appMetadata.provider like "google", "apple"
            // Email users have provider = "email"
            val provider = user.appMetadata?.get("provider")?.toString()?.removeSurrounding("\"")
            provider == "email" || provider == null
        } catch (_: Exception) { false }

    /** Update the current user's password. Requires them to be signed in. */
    suspend fun updatePassword(newPassword: String): Result<Unit> {
        return try {
            client.auth.updateUser {
                password = newPassword
            }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    suspend fun resetPassword(email: String): Result<Unit> {
        val email = email.normalizeEmail()
        return try {
            client.auth.resetPasswordForEmail(email)
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    suspend fun deleteAccount(): Result<Unit> {
        return try {
            val user = client.auth.currentUserOrNull()
                ?: return Result.failure(Exception("Not authenticated"))
            val token = client.auth.currentAccessTokenOrNull()
                ?: return Result.failure(Exception("No access token"))

            val httpClient = createHttpClient()
            val baseUrl = serverBaseUrl()
            val response = httpClient.delete("$baseUrl/api/account") {
                header("Authorization", "Bearer $token")
                contentType(ContentType.Application.Json)
                setBody("""{"userId":"${user.id}"}""")
            }

            if (response.status.isSuccess()) {
                client.auth.signOut()
                Result.success(Unit)
            } else {
                val body = response.bodyAsText()
                Result.failure(Exception("Failed to delete account: $body"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}
